import { Injectable } from '@angular/core';

import { createEffect, Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { switchMap, map, catchError, tap, withLatestFrom } from 'rxjs/operators';
import { Update } from '@ngrx/entity';
import { of } from 'rxjs';

import { EntitiesService } from '@apps/auth/domain/entities.service';
import { NotfGravity, NotificationService, NotifType } from '@common/services';
import { Entity } from '@apps/auth/domain/models/back/entity';
import * as fromBackAuthEntitiesExternalActions from '@apps/back/store/auth/entities/external/entities-external.actions';
import * as fromBackAuthEntitiesInternalActions from '@apps/back/store/auth/entities/internal/entities-internal.actions';
import { IAuroraState } from '@apps/aurora.state';
import { User } from '@apps/auth/domain/models/back';

import * as fromBackAuthEntitiesActions from './entities.actions';
import { getAllUsersExternal } from '../users/external/users-external.selectors';

@Injectable()
export class BackAuthEntitiesEffects
{
    constructor (
        private actions: Actions,
        private entitiesService: EntitiesService,
        private notificationService: NotificationService,
        private store: Store<IAuroraState>
    )
    { }

    backAuthEntityIdentityUpdateRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBackAuthEntitiesActions.BackAuthEntityIdentityUpdateRequested),
                    switchMap(({ external, entity }: { external: boolean, entity: Entity }) =>
                        this.entitiesService.updateIdentity(entity)
                            .pipe(
                                map((updatedEntity: Entity) =>
                                    fromBackAuthEntitiesActions.BackAuthEntityIdentityUpdateSucceeded({
                                        external: external,
                                        entity: updatedEntity
                                    })
                                ),
                                catchError(() =>
                                    of(fromBackAuthEntitiesActions.BackAuthEntityIdentityUpdateFailed())
                                )
                            )
                    )
                )
    );

    backAuthEntityIdentityUpdateSucceeded = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthEntitiesActions.BackAuthEntityIdentityUpdateSucceeded),
                withLatestFrom(
                    this.store.select(getAllUsersExternal),
                    ({ external, entity }, users) => ({
                        external,
                        entity,
                        users
                    })
                ),
                tap(() =>
                {
                    this.notificationService.notify([
                        'ADMIN.ENTITIES.ACTIONS.EDITENTITY.NOTF.TITLE',
                        'ADMIN.ENTITIES.ACTIONS.EDITENTITY.NOTF.MSGSUCCESS'
                    ],
                    NotfGravity.success,
                    NotifType.SNACKBAR
                    );
                }),
                map(({ external, entity, users }: { external: boolean, entity: Entity, users: User[] }) =>
                {
                    const changes: Partial<Entity> = {
                        entityShortId: entity.entityShortId,
                        name: entity.name,
                        entityErpId: entity.entityErpId,
                        cisoId: entity.cisoId,
                        dpoId: entity.dpoId
                    };

                    changes.cisoContact = users.find((user: User): boolean =>
                        user.id === entity.cisoId
                    );
                    changes.dpoContact = users.find((user: User): boolean =>
                        user.id === entity.dpoId
                    );

                    const updateEntity: Update<Entity> = {
                        id: entity.id,
                        changes
                    };

                    if (external)
                    {
                        return fromBackAuthEntitiesExternalActions.BackAuthEntityExternalUpdated({
                            entity: updateEntity
                        });
                    }

                    return fromBackAuthEntitiesInternalActions.BackAuthEntityInternalUpdated({
                        entity: updateEntity
                    });
                })
            )
    );

    backAuthEntityIdentityUpdateFailed = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthEntitiesActions.BackAuthEntityIdentityUpdateFailed),
                tap(() =>
                {
                    this.notificationService.notify([
                        'ADMIN.ENTITIES.ACTIONS.EDITENTITY.NOTF.TITLE',
                        'ADMIN.ENTITIES.ACTIONS.EDITENTITY.NOTF.MSGFAILED'
                    ],
                    NotfGravity.danger,
                    NotifType.SNACKBAR
                    );
                })
            ),
    { dispatch: false }
    );

    backAuthEntitySdmAliasUploadRequested = createEffect(
        () =>
            this.actions
                .pipe(
                    ofType(fromBackAuthEntitiesActions.BackAuthEntitySdmAliasUploadRequested),
                    switchMap(({ file }) =>
                        this.entitiesService.uploadSdmAlias(file)
                    )
                ),
        { dispatch: false }
    );

    backAuthEntityHabsUpdateRequested = createEffect(() =>
        this.actions
            .pipe(
                ofType(fromBackAuthEntitiesActions.BackAuthEntityHabsUpdateRequested),
                switchMap(({ external, entityId, habs }) =>
                    this.entitiesService.replaceAllHabilitations(entityId, habs)
                        .pipe(
                            map((entity: Entity) =>
                            {
                                const updateEntity: Update<Entity> = {
                                    id: entity.id,
                                    changes: {
                                        applications: entity.applications
                                    }
                                };

                                return external ?
                                    fromBackAuthEntitiesExternalActions.BackAuthEntityExternalUpdated({ entity: updateEntity }) :
                                    fromBackAuthEntitiesInternalActions.BackAuthEntityInternalUpdated({ entity: updateEntity });
                            })
                        )
                )
            )
    );
}
